/**
 * DutyManagerPage — لوحة مدير المناوبة (V2)
 *
 * Sidebar Navigation (13 sections in 4 groups):
 *
 * ── المناوبة ──────────────────────────────────────────────────────
 *  1. النبضة           — KPIs + category distribution
 *  2. بيانات المناوبة  — 11 staff roles entry
 *
 * ── الأحداث ───────────────────────────────────────────────────────
 *  3. سلامة المريض
 *  4. استمرارية الخدمة
 *  5. الموارد البشرية
 *  6. أمني / جنائي
 *  7. البنية التحتية
 *  8. الأحداث المصعدة
 *  9. أحداثي
 *
 * ── إجراءات ───────────────────────────────────────────────────────
 * 10. شكاوى المناوبة
 * 11. حدث جديد
 * 12. تقرير المناوبة
 *
 * ── الجودة ────────────────────────────────────────────────────────
 * 13. تحليل السبب الجذري
 */

import { useState, useMemo, useCallback } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@munawb/supabase';
import type { IncidentWithRelations, DutyShiftRow, ProfileWithDept, NotificationRow } from '@munawb/types';
import type { CreateIncidentRequest, CreateComplaintRequest, IncidentActionRequest, ActionType } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatSlaTime, formatDate } from '../lib/utils';

/* ═══════════════════════════════════════════════════════════════
   Types
══════════════════════════════════════════════════════════════════ */

type SectionId =
  | 'pulse'
  | 'shift-data'
  | 'safety'
  | 'continuity'
  | 'hr'
  | 'security'
  | 'infrastructure'
  | 'escalated'
  | 'my-events'
  | 'complaints'
  | 'new-event'
  | 'report'
  | 'rca-done';

interface ActiveShift {
  id:            string;
  shift_id:      string;
  shift_date:    string;
  period:        string;
  start_time:    string;
  end_time:      string;
  status:        string;
  report_saved:  boolean;
  report_content?: string;
}

interface StaffEntry {
  role_title: string;
  staff_name: string;
  sort_order: number;
}

interface ComplaintRow {
  id:           string;
  comp_name:    string;
  comp_type:    string;
  comp_phone?:  string;
  target_dept?: string;
  details:      string;
  action_taken?: string;
  status:       string;
  risk_level?:  string;
  created_at:   string;
}

interface IncidentLog {
  id:          string;
  action_type: string;
  action_name?: string;
  description?: string;
  created_by:  string;
  created_at:  string;
  profiles?:   { name: string } | null;
}

interface DashboardData {
  allIncidents:    IncidentWithRelations[];
  patientSafety:   IncidentWithRelations[];
  continuity:      IncidentWithRelations[];
  hr:              IncidentWithRelations[];
  security:        IncidentWithRelations[];
  infrastructure:  IncidentWithRelations[];
  escalated:       IncidentWithRelations[];
  myEvents:        IncidentWithRelations[];
  rcaDone:         IncidentWithRelations[];
  complaints:      ComplaintRow[];
  kpi: {
    total:     number;
    responded: number;
    closed:    number;
    slaRate:   number;
  };
}

/* ═══════════════════════════════════════════════════════════════
   Constants
══════════════════════════════════════════════════════════════════ */

const PERIODS = ['صباحية', 'مسائية', 'ليلية'] as const;

const DUTY_STAFF_ROLES: string[] = [
  'مدير المناوبة',
  'مدير خدمات المرضى',
  'مسؤول الأمن',
  'مدير خدمات الدعم',
  'مدير خدمات التمريض',
  'طبيب المناوبة الرئيسي',
  'طبيب الطوارئ',
  'طبيب الباطنة',
  'مدير الصيدلية',
  'مشرف المختبر',
  'مسؤول الأشعة',
];

const CATEGORY_TILES = [
  { key: 'patientSafety',  label: 'سلامة المريض',      color: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',    icon: '🏥' },
  { key: 'continuity',     label: 'استمرارية الخدمة',   color: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700', icon: '🔄' },
  { key: 'hr',             label: 'الموارد البشرية',     color: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700', icon: '👥' },
  { key: 'security',       label: 'أمني / جنائي',        color: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700', icon: '🔒' },
  { key: 'infrastructure', label: 'البنية التحتية',       color: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700', icon: '🏗️' },
  { key: 'escalated',      label: 'مصعدة',               color: 'bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700',  icon: '⬆️' },
  { key: 'rcaDone',        label: 'تحليل جذري مكتمل',    color: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700', icon: '✅' },
] as const;

const SIDEBAR_GROUPS = [
  {
    label: 'المناوبة',
    items: [
      { id: 'pulse' as SectionId,     label: 'النبضة',          icon: '📊' },
      { id: 'shift-data' as SectionId, label: 'بيانات المناوبة', icon: '📋' },
    ],
  },
  {
    label: 'الأحداث',
    items: [
      { id: 'safety' as SectionId,        label: 'سلامة المريض',     icon: '🏥' },
      { id: 'continuity' as SectionId,    label: 'استمرارية الخدمة', icon: '🔄' },
      { id: 'hr' as SectionId,            label: 'الموارد البشرية',   icon: '👥' },
      { id: 'security' as SectionId,      label: 'أمني / جنائي',      icon: '🔒' },
      { id: 'infrastructure' as SectionId,label: 'البنية التحتية',    icon: '🏗️' },
      { id: 'escalated' as SectionId,     label: 'المصعدة',           icon: '⬆️' },
      { id: 'my-events' as SectionId,     label: 'أحداثي',            icon: '👤' },
    ],
  },
  {
    label: 'إجراءات',
    items: [
      { id: 'complaints' as SectionId,  label: 'شكاوى المناوبة',  icon: '📝' },
      { id: 'new-event' as SectionId,   label: 'حدث جديد',        icon: '➕' },
      { id: 'report' as SectionId,      label: 'تقرير المناوبة',   icon: '📄' },
    ],
  },
  {
    label: 'الجودة',
    items: [
      { id: 'rca-done' as SectionId, label: 'تحليل السبب الجذري', icon: '🔍' },
    ],
  },
];

const RISK_SLA_MAP: Record<string, number> = {
  'منخفض': 240,
  'متوسط': 120,
  'عالي':  60,
  'مرتفع': 60,
  'حرج':   30,
};

/* ═══════════════════════════════════════════════════════════════
   Helpers
══════════════════════════════════════════════════════════════════ */

function todayISO() { return new Date().toISOString().slice(0, 10); }
function nowTime()  { return new Date().toTimeString().slice(0, 5); }

function calcRiskLevel(likelihood: number, impact: number): string {
  const score = likelihood * impact;
  if (score <= 4)  return 'منخفض';
  if (score <= 9)  return 'متوسط';
  if (score <= 16) return 'عالي';
  return 'حرج';
}

function isShiftEnded(endTime: string, shiftDate: string): boolean {
  const [h, m] = endTime.split(':').map(Number);
  const end = new Date(shiftDate);
  end.setHours(h, m, 0, 0);
  if (h < 8) end.setDate(end.getDate() + 1);
  return Date.now() > end.getTime();
}

function getMainCat(incident: IncidentWithRelations): string {
  return (incident as any).categories?.main_cat ?? (incident as any).event_type ?? '';
}

function categorizeDashboard(
  incidents: IncidentWithRelations[],
  complaints: ComplaintRow[],
  userId: string
): DashboardData {
  const patientSafety   = incidents.filter(i => getMainCat(i).includes('سلامة'));
  const continuity      = incidents.filter(i => getMainCat(i).includes('استمرارية'));
  const hr              = incidents.filter(i => getMainCat(i).includes('بشرية') || getMainCat(i).includes('موارد'));
  const security        = incidents.filter(i => getMainCat(i).includes('أمني') || getMainCat(i).includes('جنائي'));
  const infrastructure  = incidents.filter(i => getMainCat(i).includes('بنية') || getMainCat(i).includes('تحتية'));
  const escalated       = incidents.filter(i => i.status === 'مصعد');
  const myEvents        = incidents.filter(i => i.reporter_id === userId);
  const rcaDone         = incidents.filter(i => i.status === 'RCA Completed' || i.status === 'مغلق بعد RCA');

  const closed   = incidents.filter(i => i.status === 'مغلق' || i.status === 'RCA Completed' || i.status === 'مغلق بعد RCA').length;
  const responded = incidents.filter(i => i.status !== 'جديد').length;

  const slaBreaches = incidents.filter(i => {
    if (i.status === 'مغلق' || i.status === 'RCA Completed') return false;
    const sla = RISK_SLA_MAP[i.risk_level ?? 'منخفض'] ?? 240;
    const elapsed = (Date.now() - new Date(i.created_at).getTime()) / 60_000;
    return elapsed > sla;
  }).length;
  const slaRate = incidents.length
    ? Math.round(((incidents.length - slaBreaches) / incidents.length) * 100)
    : 100;

  return {
    allIncidents: incidents,
    patientSafety,
    continuity,
    hr,
    security,
    infrastructure,
    escalated,
    myEvents,
    rcaDone,
    complaints,
    kpi: { total: incidents.length, responded, closed, slaRate },
  };
}

/* ═══════════════════════════════════════════════════════════════
   useDutyDashboard
══════════════════════════════════════════════════════════════════ */

function useDutyDashboard(activeShift: ActiveShift | null, userId: string) {
  return useQuery<DashboardData>({
    queryKey: ['duty-dashboard', activeShift?.id, userId],
    queryFn: async () => {
      // Build time range
      let startISO: string | null = null;
      let endISO:   string | null = null;
      if (activeShift) {
        const [sh, sm] = activeShift.start_time.split(':').map(Number);
        const [eh, em] = activeShift.end_time.split(':').map(Number);
        const base = new Date(activeShift.shift_date);
        const startDate = new Date(base.getFullYear(), base.getMonth(), base.getDate(), sh, sm);
        const endDate   = new Date(base.getFullYear(), base.getMonth(), base.getDate(), eh, em);
        if (endDate <= startDate) endDate.setDate(endDate.getDate() + 1);
        startISO = startDate.toISOString();
        endISO   = endDate.toISOString();
      }

      // Fetch incidents
      let q = supabase
        .from('incidents')
        .select('*, categories:category_id(*), departments:dept_id(name), reporter:reporter_id(name)')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (startISO && endISO) {
        q = q.gte('created_at', startISO).lte('created_at', endISO);
      }
      const { data: incidents, error: incErr } = await q;
      if (incErr) throw incErr;

      // Fetch complaints
      let complaints: ComplaintRow[] = [];
      if (activeShift?.id) {
        const { data: comp } = await supabase
          .from('duty_complaints')
          .select('*')
          .eq('shift_id', activeShift.id)
          .order('created_at', { ascending: false });
        complaints = (comp as ComplaintRow[]) ?? [];
      }

      return categorizeDashboard(
        (incidents as unknown as IncidentWithRelations[]) ?? [],
        complaints,
        userId
      );
    },
    refetchInterval: 60_000,
    enabled: true,
  });
}

/* ═══════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════════ */

export default function DutyManagerPage() {
  const { profile } = useAuthStore();
  const qc = useQueryClient();
  const [activeSection, setActiveSection] = useState<SectionId>('pulse');
  const [sidebarOpen, setSidebarOpen]     = useState(false);
  const [showLogout,  setShowLogout]      = useState(false);
  const [showProfile, setShowProfile]     = useState(false);
  const [showNotif,   setShowNotif]       = useState(false);

  // Event details / RCA modals
  const [selectedIncident, setSelectedIncident] = useState<IncidentWithRelations | null>(null);
  const [showEventModal,   setShowEventModal]    = useState(false);
  const [showRcaModal,     setShowRcaModal]      = useState(false);

  /* ── Active Shift ─────────────────────────────────────────────── */
  const { data: activeShift, isLoading: shiftLoading } = useQuery<ActiveShift | null>({
    queryKey: ['duty-shift-active', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const { data } = await supabase
        .from('duty_shifts')
        .select('*')
        .eq('manager_id', profile.id)
        .eq('shift_date', todayISO())
        .in('status', ['active', 'ended', 'report_pending'])
        .maybeSingle();
      return (data as ActiveShift | null);
    },
    refetchInterval: 60_000,
  });

  /* ── Dashboard Data ───────────────────────────────────────────── */
  const { data: dashboard, isLoading: dashLoading } = useDutyDashboard(
    activeShift ?? null,
    profile?.id ?? ''
  );

  /* ── Notifications ────────────────────────────────────────────── */
  const { data: notifications = [] } = useNotifications(profile?.id ?? '', null, true);
  const { mutate: markRead } = useMarkNotificationsRead(profile?.id ?? '');
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleSignOut = async () => {
    setShowLogout(false);
    await supabase.auth.signOut();
  };

  const openEvent = (inc: IncidentWithRelations) => {
    setSelectedIncident(inc);
    setShowEventModal(true);
  };
  const openRca = (inc: IncidentWithRelations) => {
    setSelectedIncident(inc);
    setShowRcaModal(true);
  };

  /* ── Section badge counts ─────────────────────────────────────── */
  const sectionBadges = useMemo<Partial<Record<SectionId, number>>>(() => {
    if (!dashboard) return {};
    return {
      pulse:          dashboard.kpi.total,
      safety:         dashboard.patientSafety.length,
      continuity:     dashboard.continuity.length,
      hr:             dashboard.hr.length,
      security:       dashboard.security.length,
      infrastructure: dashboard.infrastructure.length,
      escalated:      dashboard.escalated.length,
      'my-events':    dashboard.myEvents.length,
      complaints:     dashboard.complaints.length,
      'rca-done':     dashboard.rcaDone.length,
    };
  }, [dashboard]);

  /* ── Render active section ────────────────────────────────────── */
  const renderSection = () => {
    if (dashLoading || shiftLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      );
    }
    const d = dashboard;
    const sharedProps = { onOpenEvent: openEvent, onOpenRca: openRca };

    switch (activeSection) {
      case 'pulse':
        return <PulseSection dashboard={d} />;
      case 'shift-data':
        return <ShiftDataSection activeShift={activeShift ?? null} profile={profile} onShiftUpdated={() => qc.invalidateQueries({ queryKey: ['duty-shift-active'] })} />;
      case 'safety':
        return <EventGridSection title="أحداث سلامة المريض" incidents={d?.patientSafety ?? []} {...sharedProps} />;
      case 'continuity':
        return <EventGridSection title="أحداث استمرارية الخدمة" incidents={d?.continuity ?? []} {...sharedProps} />;
      case 'hr':
        return <EventGridSection title="أحداث الموارد البشرية" incidents={d?.hr ?? []} {...sharedProps} />;
      case 'security':
        return <EventGridSection title="أحداث أمنية / جنائية" incidents={d?.security ?? []} {...sharedProps} />;
      case 'infrastructure':
        return <EventGridSection title="أحداث البنية التحتية" incidents={d?.infrastructure ?? []} {...sharedProps} />;
      case 'escalated':
        return <EventGridSection title="الأحداث المصعدة" incidents={d?.escalated ?? []} {...sharedProps} />;
      case 'my-events':
        return <EventGridSection title="أحداثي" incidents={d?.myEvents ?? []} {...sharedProps} />;
      case 'complaints':
        return <ComplaintsSection activeShift={activeShift ?? null} complaints={d?.complaints ?? []} onRefresh={() => qc.invalidateQueries({ queryKey: ['duty-dashboard'] })} />;
      case 'new-event':
        return <NewEventSection profile={profile} onRefresh={() => qc.invalidateQueries({ queryKey: ['duty-dashboard'] })} />;
      case 'report':
        return <ReportSection activeShift={activeShift ?? null} dashboard={d ?? null} profile={profile} onRefresh={() => qc.invalidateQueries({ queryKey: ['duty-shift-active'] })} />;
      case 'rca-done':
        return <RcaDoneSection incidents={d?.rcaDone ?? []} {...sharedProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 flex flex-col" dir="rtl">

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="px-4 h-14 flex items-center justify-between">
          {/* Logo + title */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setSidebarOpen(v => !v)}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <div>
              <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب</span>
              <span className="mr-2 text-xs text-slate-500 dark:text-slate-400">— مدير المناوبة</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Shift badge */}
            {activeShift && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                مناوبة نشطة
              </span>
            )}
            {/* Notifications */}
            <button
              className="relative p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setShowNotif(true)}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {/* Profile */}
            <button
              onClick={() => setShowProfile(true)}
              className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:scale-110 transition border border-slate-200 dark:border-slate-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1118.88 6.196M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button onClick={() => setShowLogout(true)} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      {/* ── Body (sidebar + content) ────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Sidebar overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 right-0 z-20 w-64
          bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700
          flex flex-col transition-transform duration-300 lg:translate-x-0 overflow-y-auto
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `} style={{ top: '56px' }}>
          <nav className="flex-1 p-3 space-y-4">
            {SIDEBAR_GROUPS.map(group => (
              <div key={group.label}>
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2 mb-1">
                  {group.label}
                </p>
                <ul className="space-y-0.5">
                  {group.items.map(item => {
                    const count = sectionBadges[item.id];
                    const active = activeSection === item.id;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors text-right
                            ${active
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                        >
                          <span className="text-base leading-none">{item.icon}</span>
                          <span className="flex-1">{item.label}</span>
                          {count !== undefined && count > 0 && (
                            <span className={`min-w-[20px] h-5 px-1 rounded-full text-[10px] font-black flex items-center justify-center
                              ${active ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                              {count}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {renderSection()}
        </main>
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}
      {showEventModal && selectedIncident && (
        <EventDetailsModal
          incident={selectedIncident}
          onClose={() => setShowEventModal(false)}
          onRefresh={() => qc.invalidateQueries({ queryKey: ['duty-dashboard'] })}
        />
      )}
      {showRcaModal && selectedIncident && (
        <RcaViewModal
          incident={selectedIncident}
          onClose={() => setShowRcaModal(false)}
        />
      )}
      {showNotif && (
        <NotificationsModal
          notifications={notifications}
          onMarkRead={(ids) => markRead(ids)}
          onClose={() => setShowNotif(false)}
        />
      )}
      {showProfile && (
        <ProfileModal profile={profile} onClose={() => setShowProfile(false)} />
      )}
      {showLogout && (
        <LogoutModal onConfirm={handleSignOut} onClose={() => setShowLogout(false)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PulseSection — النبضة
══════════════════════════════════════════════════════════════════ */

function PulseSection({ dashboard }: { dashboard: DashboardData | undefined }) {
  if (!dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }
  const { kpi } = dashboard;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-black text-slate-800 dark:text-white">النبضة — نظرة عامة</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card">
          <p className="text-3xl font-black text-slate-800 dark:text-white">{kpi.total}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">إجمالي الأحداث</p>
        </div>
        <div className="kpi-card">
          <p className="text-3xl font-black text-blue-600 dark:text-blue-400">{kpi.responded}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">جرى التعامل معها</p>
        </div>
        <div className="kpi-card">
          <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">{kpi.closed}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">مغلقة</p>
        </div>
        <div className="kpi-card">
          <p className={`text-3xl font-black ${kpi.slaRate >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {kpi.slaRate}%
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">معدل الامتثال للـ SLA</p>
        </div>
      </div>

      {/* Category Tiles */}
      <div className="glass-panel p-5">
        <h3 className="text-sm font-bold text-slate-600 dark:text-slate-400 mb-4">توزيع الأحداث حسب الفئة</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {CATEGORY_TILES.map(tile => {
            const count = (dashboard as any)[tile.key]?.length ?? 0;
            return (
              <div key={tile.key} className={`rounded-xl border p-4 flex flex-col items-center gap-2 ${tile.color}`}>
                <span className="text-2xl">{tile.icon}</span>
                <p className="text-2xl font-black text-slate-800 dark:text-white">{count}</p>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 text-center leading-tight">{tile.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ShiftDataSection — بيانات المناوبة (11 roles)
══════════════════════════════════════════════════════════════════ */

function ShiftDataSection({
  activeShift,
  profile,
  onShiftUpdated,
}: {
  activeShift: ActiveShift | null;
  profile: ProfileWithDept | null;
  onShiftUpdated: () => void;
}) {
  const qc = useQueryClient();

  // Shift creation form
  const [shiftDate,   setShiftDate]   = useState(todayISO());
  const [period,      setPeriod]      = useState<string>('صباحية');
  const [startTime,   setStartTime]   = useState(nowTime());
  const [endTime,     setEndTime]     = useState('');
  const [staffMap,    setStaffMap]    = useState<Record<string, string>>(() =>
    Object.fromEntries(DUTY_STAFF_ROLES.map(r => [r, '']))
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // Existing shift staff
  const { data: existingStaff = [] } = useQuery<StaffEntry[]>({
    queryKey: ['shift-staff', activeShift?.id],
    queryFn: async () => {
      if (!activeShift?.id) return [];
      const { data } = await supabase
        .from('duty_shift_staff')
        .select('role_title, staff_name, sort_order')
        .eq('shift_id', activeShift.id)
        .order('sort_order');
      return (data as StaffEntry[]) ?? [];
    },
    enabled: !!activeShift?.id,
  });

  const handleCreateShift = async () => {
    if (!profile?.id) return;
    const staff = DUTY_STAFF_ROLES
      .map((role, i) => ({ role_title: role, staff_name: staffMap[role] ?? '', sort_order: i }))
      .filter(s => s.staff_name.trim());
    if (!endTime) { setError('يرجى تحديد وقت انتهاء المناوبة'); return; }
    setSaving(true); setError('');
    try {
      const { data: result, error: fnErr } = await supabase.functions.invoke('duty-shifts-create', {
        body: {
          shift_date: shiftDate,
          period,
          start_time: startTime,
          end_time:   endTime,
          staff,
        },
      });
      if (fnErr) throw fnErr;
      onShiftUpdated();
      qc.invalidateQueries({ queryKey: ['shift-staff'] });
    } catch (e: any) {
      setError(e.message ?? 'حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  if (activeShift) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-800 dark:text-white">بيانات المناوبة</h2>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            مناوبة نشطة — {activeShift.period}
          </span>
        </div>

        <div className="glass-panel p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">التاريخ</p>
              <p className="font-bold text-slate-800 dark:text-white">{activeShift.shift_date}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">الفترة</p>
              <p className="font-bold text-slate-800 dark:text-white">{activeShift.period}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">الوقت</p>
              <p className="font-bold text-slate-800 dark:text-white">{activeShift.start_time} – {activeShift.end_time}</p>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-600 dark:text-slate-400 mb-3">طاقم المناوبة</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DUTY_STAFF_ROLES.map(role => {
              const member = existingStaff.find(s => s.role_title === role);
              return (
                <div key={role} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div className="flex-1">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{role}</p>
                    <p className="font-semibold text-slate-800 dark:text-white text-sm">
                      {member?.staff_name || <span className="text-slate-400 italic text-xs">غير محدد</span>}
                    </p>
                  </div>
                  {member?.staff_name && (
                    <span className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-black flex items-center justify-center">
                      {member.staff_name.split(' ').map(w => w[0]).join('').slice(0,2)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-black text-slate-800 dark:text-white">بدء مناوبة جديدة</h2>

      <div className="glass-panel p-5 space-y-5">
        {/* Shift metadata */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">التاريخ</label>
            <input type="date" value={shiftDate} onChange={e => setShiftDate(e.target.value)} className="input-field w-full" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">الفترة</label>
            <select value={period} onChange={e => setPeriod(e.target.value)} className="input-field w-full">
              {PERIODS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">وقت البداية</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="input-field w-full" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">وقت الانتهاء</label>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="input-field w-full" />
          </div>
        </div>

        {/* Staff entries */}
        <div>
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">طاقم المناوبة</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DUTY_STAFF_ROLES.map(role => (
              <div key={role}>
                <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">{role}</label>
                <input
                  type="text"
                  placeholder={`اسم ${role}`}
                  value={staffMap[role] ?? ''}
                  onChange={e => setStaffMap(prev => ({ ...prev, [role]: e.target.value }))}
                  className="input-field w-full"
                />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}

        <div className="flex justify-end">
          <button onClick={handleCreateShift} disabled={saving} className="btn-primary px-6">
            {saving ? <span className="spinner w-4 h-4" /> : 'بدء المناوبة'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EventGridSection — عرض قائمة أحداث حسب الفئة
══════════════════════════════════════════════════════════════════ */

function EventGridSection({
  title,
  incidents,
  onOpenEvent,
  onOpenRca,
}: {
  title: string;
  incidents: IncidentWithRelations[];
  onOpenEvent: (i: IncidentWithRelations) => void;
  onOpenRca:   (i: IncidentWithRelations) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-black text-slate-800 dark:text-white">{title}</h2>
        <span className="px-2.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">
          {incidents.length}
        </span>
      </div>

      {incidents.length === 0 ? (
        <div className="glass-panel p-12 flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
          <svg className="w-12 h-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">لا توجد أحداث في هذه الفئة</p>
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map(inc => (
            <IncidentCard
              key={inc.id}
              incident={inc}
              onOpenEvent={onOpenEvent}
              onOpenRca={onOpenRca}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── IncidentCard ─────────────────────────────────────────────── */

function IncidentCard({
  incident: i,
  onOpenEvent,
  onOpenRca,
}: {
  incident: IncidentWithRelations;
  onOpenEvent: (i: IncidentWithRelations) => void;
  onOpenRca:   (i: IncidentWithRelations) => void;
}) {
  const riskColors = RISK_LEVEL_COLORS[i.risk_level ?? 'منخفض'] ?? RISK_LEVEL_COLORS['منخفض'];
  const slaMinutes = RISK_SLA_MAP[i.risk_level ?? 'منخفض'] ?? 240;
  const sla = formatSlaTime(i.created_at, slaMinutes, i.status);
  const dept = (i as any).departments?.name ?? i.department?.name ?? '';
  const reporter = (i as any).reporter?.name ?? '';

  return (
    <div className={`glass-panel p-4 border ${riskColors.bg} transition-all hover:shadow-md`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${riskColors.badge}`}>
              {i.risk_level}
            </span>
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
              {i.status}
            </span>
            {dept && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                {dept}
              </span>
            )}
          </div>
          <p className="text-sm font-bold text-slate-800 dark:text-white mb-1 truncate">{i.event_type}</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">{i.description}</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-[10px] text-slate-400">{formatDate(i.created_at)}</span>
            {reporter && <span className="text-[10px] text-slate-400">بواسطة: {reporter}</span>}
            {sla && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sla.breached ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'}`}>
                SLA: {sla.remaining}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => onOpenEvent(i)}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            التفاصيل
          </button>
          {(i.status === 'RCA Completed' || i.status === 'مغلق بعد RCA') && (
            <button
              onClick={() => onOpenRca(i)}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              عرض RCA
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ComplaintsSection — شكاوى المناوبة
══════════════════════════════════════════════════════════════════ */

const COMP_TYPES  = ['مريض', 'مرافق', 'زائر', 'موظف', 'أخرى'] as const;
const RISK_LEVELS = ['منخفض', 'متوسط', 'عالي', 'حرج'] as const;
const LIKELIHOOD_LABELS = ['نادر', 'غير محتمل', 'محتمل', 'مرجح', 'شبه مؤكد'];
const IMPACT_LABELS     = ['لا أثر', 'طفيف', 'متوسط', 'جسيم', 'كارثي'];

function ComplaintsSection({
  activeShift,
  complaints,
  onRefresh,
}: {
  activeShift: ActiveShift | null;
  complaints:  ComplaintRow[];
  onRefresh:   () => void;
}) {
  const [showForm,    setShowForm]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [compType,    setCompType]    = useState<string>('مريض');
  const [compName,    setCompName]    = useState('');
  const [compPhone,   setCompPhone]   = useState('');
  const [targetDept,  setTargetDept]  = useState('');
  const [details,     setDetails]     = useState('');
  const [likelihood,  setLikelihood]  = useState(1);
  const [impact,      setImpact]      = useState(1);

  const riskScore = likelihood * impact;
  const riskLevel = calcRiskLevel(likelihood, impact);

  const handleSubmit = async () => {
    if (!activeShift?.id) { setError('لا توجد مناوبة نشطة'); return; }
    if (!compName.trim() || !details.trim()) { setError('يرجى تعبئة الحقول المطلوبة'); return; }
    setSaving(true); setError('');
    try {
      const body: CreateComplaintRequest = {
        shift_id:    activeShift.id,
        comp_type:   compType as any,
        comp_name:   compName,
        comp_phone:  compPhone || undefined,
        target_dept: targetDept || undefined,
        details,
        risk_score:  riskScore,
        risk_level:  riskLevel as any,
      };
      const { error: fnErr } = await supabase.functions.invoke('complaints-create', { body });
      if (fnErr) {
        // Fallback: direct DB insert
        const { error: dbErr } = await supabase.from('duty_complaints').insert({
          shift_id:    activeShift.id,
          comp_type:   compType,
          comp_name:   compName,
          comp_phone:  compPhone || null,
          target_dept: targetDept || null,
          details,
          risk_score:  riskScore,
          risk_level:  riskLevel,
          status:      'مفتوح',
        });
        if (dbErr) throw dbErr;
      }
      setCompName(''); setCompPhone(''); setTargetDept(''); setDetails('');
      setLikelihood(1); setImpact(1);
      setShowForm(false);
      onRefresh();
    } catch (e: any) {
      setError(e.message ?? 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-slate-800 dark:text-white">شكاوى المناوبة</h2>
        {activeShift && (
          <button onClick={() => setShowForm(v => !v)} className="btn-primary text-sm px-4">
            {showForm ? 'إلغاء' : '+ شكوى جديدة'}
          </button>
        )}
      </div>

      {!activeShift && (
        <div className="glass-panel p-6 text-center text-slate-500 dark:text-slate-400 text-sm">
          يجب بدء مناوبة أولاً لتسجيل الشكاوى
        </div>
      )}

      {/* New Complaint Form */}
      {showForm && (
        <div className="glass-panel p-5 space-y-4 border border-blue-200 dark:border-blue-800">
          <h3 className="font-bold text-slate-700 dark:text-slate-300">تفاصيل الشكوى</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">نوع الشاكي *</label>
              <select value={compType} onChange={e => setCompType(e.target.value)} className="input-field w-full">
                {COMP_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">اسم الشاكي *</label>
              <input type="text" placeholder="الاسم الكامل" value={compName} onChange={e => setCompName(e.target.value)} className="input-field w-full" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">رقم الاتصال</label>
              <input type="tel" placeholder="05xxxxxxxx" value={compPhone} onChange={e => setCompPhone(e.target.value)} className="input-field w-full" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">القسم المعني</label>
              <input type="text" placeholder="اسم القسم" value={targetDept} onChange={e => setTargetDept(e.target.value)} className="input-field w-full" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">تفاصيل الشكوى *</label>
            <textarea rows={3} placeholder="وصف الشكوى..." value={details} onChange={e => setDetails(e.target.value)} className="input-field w-full resize-none" />
          </div>

          {/* Risk Matrix */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-2">
                الاحتمالية: <span className="text-blue-600">{LIKELIHOOD_LABELS[likelihood - 1]}</span>
              </label>
              <input type="range" min={1} max={5} value={likelihood} onChange={e => setLikelihood(Number(e.target.value))} className="w-full" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-2">
                التأثير: <span className="text-blue-600">{IMPACT_LABELS[impact - 1]}</span>
              </label>
              <input type="range" min={1} max={5} value={impact} onChange={e => setImpact(Number(e.target.value))} className="w-full" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">درجة المخاطرة:</span>
            <span className={`text-xs font-black px-3 py-1 rounded-full ${RISK_LEVEL_COLORS[riskLevel]?.badge}`}>
              {riskLevel} ({riskScore})
            </span>
          </div>

          {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">إلغاء</button>
            <button onClick={handleSubmit} disabled={saving} className="btn-primary text-sm">
              {saving ? <span className="spinner w-4 h-4" /> : 'حفظ الشكوى'}
            </button>
          </div>
        </div>
      )}

      {/* Complaints List */}
      {complaints.length === 0 ? (
        <div className="glass-panel p-10 flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
          <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <p className="text-sm">لا توجد شكاوى مسجلة</p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => (
            <div key={c.id} className="glass-panel p-4 border border-slate-200 dark:border-slate-700">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{c.comp_name}</span>
                    <span className="text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full">{c.comp_type}</span>
                    {c.risk_level && (
                      <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${RISK_LEVEL_COLORS[c.risk_level]?.badge ?? ''}`}>
                        {c.risk_level}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{c.details}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{formatDate(c.created_at)}</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${
                  c.status === 'مغلق' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                }`}>
                  {c.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   NewEventSection — حدث جديد
══════════════════════════════════════════════════════════════════ */

function NewEventSection({
  profile,
  onRefresh,
}: {
  profile:   ProfileWithDept | null;
  onRefresh: () => void;
}) {
  const [location,    setLocation]    = useState('');
  const [eventType,   setEventType]   = useState('');
  const [description, setDescription] = useState('');
  const [likelihood,  setLikelihood]  = useState(1);
  const [impact,      setImpact]      = useState(1);
  const [isRepeated,  setIsRepeated]  = useState(false);
  const [reference,   setReference]   = useState('');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const riskScore = likelihood * impact;
  const riskLevel = calcRiskLevel(likelihood, impact);

  const handleSubmit = async () => {
    if (!location.trim() || !eventType.trim() || !description.trim()) {
      setError('يرجى تعبئة جميع الحقول المطلوبة'); return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const body: CreateIncidentRequest & { reporter_name: string } = {
        location,
        event_type:  eventType,
        description,
        likelihood,
        impact,
        is_repeated: isRepeated,
        reference:   reference || undefined,
        dept_id:     profile?.dept_id ?? undefined,
        reporter_name: profile?.name ?? '',
      };
      const { error: fnErr } = await supabase.functions.invoke('incidents-create', { body });
      if (fnErr) throw fnErr;
      setLocation(''); setEventType(''); setDescription(''); setLikelihood(1); setImpact(1);
      setIsRepeated(false); setReference('');
      setSuccess('تم تسجيل الحدث بنجاح');
      onRefresh();
    } catch (e: any) {
      setError(e.message ?? 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-black text-slate-800 dark:text-white">تسجيل حدث جديد</h2>

      <div className="glass-panel p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">الموقع *</label>
            <input type="text" placeholder="موقع الحدث" value={location} onChange={e => setLocation(e.target.value)} className="input-field w-full" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">نوع الحدث *</label>
            <input type="text" placeholder="نوع / عنوان الحدث" value={eventType} onChange={e => setEventType(e.target.value)} className="input-field w-full" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">وصف الحدث *</label>
          <textarea rows={3} placeholder="وصف تفصيلي للحدث..." value={description} onChange={e => setDescription(e.target.value)} className="input-field w-full resize-none" />
        </div>

        {/* Risk Matrix */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-2">
              الاحتمالية ({likelihood}): <span className="text-blue-600">{LIKELIHOOD_LABELS[likelihood - 1]}</span>
            </label>
            <input type="range" min={1} max={5} value={likelihood} onChange={e => setLikelihood(Number(e.target.value))} className="w-full" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-2">
              التأثير ({impact}): <span className="text-blue-600">{IMPACT_LABELS[impact - 1]}</span>
            </label>
            <input type="range" min={1} max={5} value={impact} onChange={e => setImpact(Number(e.target.value))} className="w-full" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 dark:text-slate-400">مستوى المخاطرة:</span>
          <span className={`text-xs font-black px-3 py-1 rounded-full ${RISK_LEVEL_COLORS[riskLevel]?.badge}`}>
            {riskLevel} (درجة: {riskScore})
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">مرجع / رقم الحادثة</label>
            <input type="text" placeholder="رقم مرجعي (اختياري)" value={reference} onChange={e => setReference(e.target.value)} className="input-field w-full" />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" id="repeated" checked={isRepeated} onChange={e => setIsRepeated(e.target.checked)} className="w-4 h-4 rounded" />
            <label htmlFor="repeated" className="text-sm text-slate-700 dark:text-slate-300">حدث متكرر</label>
          </div>
        </div>

        {error   && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
        {success && <p className="text-emerald-600 dark:text-emerald-400 text-sm font-bold">{success}</p>}

        <div className="flex justify-end">
          <button onClick={handleSubmit} disabled={saving} className="btn-primary px-6">
            {saving ? <span className="spinner w-4 h-4" /> : 'تسجيل الحدث'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   RcaDoneSection — تحليل السبب الجذري المكتمل
══════════════════════════════════════════════════════════════════ */

function RcaDoneSection({
  incidents,
  onOpenEvent,
  onOpenRca,
}: {
  incidents:   IncidentWithRelations[];
  onOpenEvent: (i: IncidentWithRelations) => void;
  onOpenRca:   (i: IncidentWithRelations) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-black text-slate-800 dark:text-white">تحليل السبب الجذري — مكتمل</h2>
        <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
          {incidents.length}
        </span>
      </div>

      {incidents.length === 0 ? (
        <div className="glass-panel p-12 flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
          <svg className="w-12 h-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">لا توجد أحداث مكتملة بتحليل جذري</p>
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map(inc => (
            <IncidentCard key={inc.id} incident={inc} onOpenEvent={onOpenEvent} onOpenRca={onOpenRca} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ReportSection — تقرير المناوبة
══════════════════════════════════════════════════════════════════ */

function ReportSection({
  activeShift,
  dashboard,
  profile,
  onRefresh,
}: {
  activeShift: ActiveShift | null;
  dashboard:   DashboardData | null;
  profile:     ProfileWithDept | null;
  onRefresh:   () => void;
}) {
  const [reportContent, setReportContent] = useState(activeShift?.report_content ?? '');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');

  const autoSummary = useMemo(() => {
    if (!dashboard || !activeShift) return '';
    const { kpi, allIncidents, complaints } = dashboard;
    const lines = [
      `تقرير مناوبة ${activeShift.period} — ${activeShift.shift_date}`,
      `الوقت: ${activeShift.start_time} – ${activeShift.end_time}`,
      '',
      `إجمالي الأحداث: ${kpi.total}`,
      `جرى التعامل معها: ${kpi.responded}`,
      `مغلقة: ${kpi.closed}`,
      `معدل الامتثال للـ SLA: ${kpi.slaRate}%`,
      '',
      `الشكاوى المسجلة: ${complaints.length}`,
    ];
    const byCat = [
      ['سلامة المريض',     dashboard.patientSafety.length],
      ['استمرارية الخدمة', dashboard.continuity.length],
      ['الموارد البشرية',   dashboard.hr.length],
      ['أمني/جنائي',        dashboard.security.length],
      ['البنية التحتية',    dashboard.infrastructure.length],
      ['مصعدة',             dashboard.escalated.length],
    ].filter(([, c]) => (c as number) > 0);
    if (byCat.length) {
      lines.push('', 'توزيع الأحداث:');
      byCat.forEach(([label, count]) => lines.push(`  • ${label}: ${count}`));
    }
    return lines.join('\n');
  }, [dashboard, activeShift]);

  const handleSave = async () => {
    if (!activeShift?.id) { setError('لا توجد مناوبة نشطة'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const { error: fnErr } = await supabase.functions.invoke('duty-shifts-report', {
        body: { shift_id: activeShift.id, content: reportContent, summary: autoSummary },
      });
      if (fnErr) throw fnErr;
      setSuccess('تم حفظ التقرير بنجاح');
      onRefresh();
    } catch (e: any) {
      setError(e.message ?? 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!activeShift || !dashboard || !profile) return;
    const html = buildReportHTML(activeShift, dashboard, profile, reportContent);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  if (!activeShift) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-black text-slate-800 dark:text-white">تقرير المناوبة</h2>
        <div className="glass-panel p-10 flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
          <p className="text-sm">يجب بدء مناوبة أولاً لإعداد التقرير</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-black text-slate-800 dark:text-white">تقرير المناوبة</h2>
        <div className="flex gap-2">
          <button onClick={handlePrint} className="btn-secondary text-sm px-4">
            🖨️ طباعة
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm px-4">
            {saving ? <span className="spinner w-4 h-4" /> : 'حفظ التقرير'}
          </button>
        </div>
      </div>

      {/* Auto-generated summary */}
      <div className="glass-panel p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
        <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 mb-2">ملخص تلقائي</h3>
        <pre className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">{autoSummary}</pre>
      </div>

      {/* Editable report */}
      <div className="glass-panel p-5 space-y-3">
        <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block">محتوى التقرير التفصيلي</label>
        <textarea
          rows={12}
          placeholder="أضف ملاحظاتك وتفاصيل الأحداث والإجراءات المتخذة خلال المناوبة..."
          value={reportContent}
          onChange={e => setReportContent(e.target.value)}
          className="input-field w-full resize-y font-mono text-sm leading-relaxed"
        />
      </div>

      {error   && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
      {success && <p className="text-emerald-600 dark:text-emerald-400 text-sm font-bold">{success}</p>}
    </div>
  );
}

function buildReportHTML(
  shift: ActiveShift,
  dashboard: DashboardData,
  profile: ProfileWithDept,
  content: string
): string {
  const { kpi } = dashboard;
  const now = new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>تقرير مناوبة — ${shift.shift_date}</title>
<style>
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; direction: rtl; margin: 0; padding: 20px; color: #1e293b; }
  .header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); color: white; padding: 20px 24px; border-radius: 12px; margin-bottom: 20px; }
  .header h1 { margin: 0 0 4px; font-size: 20px; }
  .header p  { margin: 0; font-size: 13px; opacity: 0.85; }
  .kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
  .kpi { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; text-align: center; }
  .kpi .value { font-size: 28px; font-weight: 900; color: #1e40af; }
  .kpi .label { font-size: 11px; color: #64748b; margin-top: 4px; }
  .section { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin-bottom: 14px; }
  .section h2 { font-size: 14px; font-weight: 700; margin: 0 0 10px; color: #1e3a5f; border-bottom: 2px solid #2563eb; padding-bottom: 6px; }
  .content { font-size: 13px; line-height: 1.8; white-space: pre-wrap; color: #334155; }
  .footer { text-align: center; font-size: 11px; color: #94a3b8; margin-top: 24px; }
  @media print { body { padding: 10px; } }
</style>
</head>
<body>
<div class="header">
  <h1>تقرير مناوبة — ${shift.period}</h1>
  <p>${shift.shift_date} | ${shift.start_time} – ${shift.end_time} | مدير المناوبة: ${profile.name}</p>
</div>
<div class="kpi-row">
  <div class="kpi"><div class="value">${kpi.total}</div><div class="label">إجمالي الأحداث</div></div>
  <div class="kpi"><div class="value">${kpi.responded}</div><div class="label">جرى التعامل معها</div></div>
  <div class="kpi"><div class="value">${kpi.closed}</div><div class="label">مغلقة</div></div>
  <div class="kpi"><div class="value">${kpi.slaRate}%</div><div class="label">امتثال SLA</div></div>
</div>
${content ? `<div class="section"><h2>ملاحظات مدير المناوبة</h2><div class="content">${content}</div></div>` : ''}
<div class="footer">مُناوِب — نظام إدارة المناوبة الإلكتروني | طُبع في ${now}</div>
</body>
</html>`;
}

/* ═══════════════════════════════════════════════════════════════
   EventDetailsModal
══════════════════════════════════════════════════════════════════ */

const ACTION_LABELS: Record<ActionType, string> = {
  update:      'تحديث الحالة',
  assign:      'إسناد',
  direct:      'توجيه مباشر',
  rca:         'إدخال RCA',
  request_rca: 'طلب RCA',
  escalate:    'تصعيد',
  close:       'إغلاق',
  reopen:      'إعادة فتح',
};

function EventDetailsModal({
  incident,
  onClose,
  onRefresh,
}: {
  incident:  IncidentWithRelations;
  onClose:   () => void;
  onRefresh: () => void;
}) {
  const qc = useQueryClient();
  const [actionType,   setActionType]   = useState<ActionType>('update');
  const [description,  setDescription]  = useState('');
  const [target,       setTarget]       = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const riskColors = RISK_LEVEL_COLORS[incident.risk_level ?? 'منخفض'] ?? RISK_LEVEL_COLORS['منخفض'];
  const dept = (incident as any).departments?.name ?? incident.department?.name ?? '';

  const { data: logs = [] } = useQuery<IncidentLog[]>({
    queryKey: ['incident-logs', incident.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('incident_logs')
        .select('*, profiles:created_by(name)')
        .eq('incident_id', incident.id)
        .order('created_at', { ascending: false });
      return (data as IncidentLog[]) ?? [];
    },
  });

  const handleAction = async () => {
    if (!description.trim()) { setError('يرجى إدخال وصف للإجراء'); return; }
    setSaving(true); setError('');
    try {
      const body: IncidentActionRequest = {
        incident_id: incident.id,
        action_type: actionType,
        description,
        target: target || undefined,
      };
      const { error: fnErr } = await supabase.functions.invoke('incidents-action', { body });
      if (fnErr) throw fnErr;
      setDescription(''); setTarget('');
      qc.invalidateQueries({ queryKey: ['incident-logs', incident.id] });
      onRefresh();
    } catch (e: any) {
      setError(e.message ?? 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`p-5 rounded-t-2xl border-b ${riskColors.bg}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-xs font-black px-2.5 py-0.5 rounded-full ${riskColors.badge}`}>{incident.risk_level}</span>
                <span className="text-xs bg-white/60 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full font-semibold">{incident.status}</span>
                {dept && <span className="text-xs bg-white/60 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full">{dept}</span>}
              </div>
              <h2 className="text-lg font-black text-slate-800 dark:text-white">{incident.event_type}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(incident.created_at)}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Incident Details */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">الوصف</p>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{incident.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 mb-0.5">الموقع</p>
              <p className="font-semibold text-slate-700 dark:text-slate-300">{incident.location}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">الاحتمالية × التأثير</p>
              <p className="font-semibold text-slate-700 dark:text-slate-300">{incident.likelihood} × {incident.impact} = {incident.likelihood * incident.impact}</p>
            </div>
          </div>

          {/* Action Panel */}
          <div className="glass-panel p-4 space-y-3 border border-blue-200 dark:border-blue-800">
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">اتخاذ إجراء</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(Object.keys(ACTION_LABELS) as ActionType[]).map(type => (
                <button
                  key={type}
                  onClick={() => setActionType(type)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors
                    ${actionType === type
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                    }`}
                >
                  {ACTION_LABELS[type]}
                </button>
              ))}
            </div>
            <textarea
              rows={2}
              placeholder="وصف الإجراء..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="input-field w-full resize-none text-sm"
            />
            {(actionType === 'direct' || actionType === 'assign') && (
              <input
                type="text"
                placeholder="الجهة / المستهدف"
                value={target}
                onChange={e => setTarget(e.target.value)}
                className="input-field w-full"
              />
            )}
            {error && <p className="text-red-600 dark:text-red-400 text-xs">{error}</p>}
            <div className="flex justify-end">
              <button onClick={handleAction} disabled={saving} className="btn-primary text-sm px-5">
                {saving ? <span className="spinner w-4 h-4" /> : 'تنفيذ الإجراء'}
              </button>
            </div>
          </div>

          {/* Logs Timeline */}
          {logs.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-bold text-slate-600 dark:text-slate-400">سجل الإجراءات</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {logs.map(log => (
                  <div key={log.id} className="flex gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-sm">
                    <div className="w-1.5 rounded-full bg-blue-400 self-stretch shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-700 dark:text-slate-300 text-xs">{log.action_name ?? log.action_type}</span>
                        <span className="text-[10px] text-slate-400">
                          {log.profiles?.name ?? 'نظام'} — {formatDate(log.created_at)}
                        </span>
                      </div>
                      {log.description && <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{log.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   RcaViewModal — عرض تحليل السبب الجذري
══════════════════════════════════════════════════════════════════ */

interface RcaRow {
  why_1:       string;
  why_2?:      string;
  why_3?:      string;
  why_4?:      string;
  root_cause:  string;
  action_plan: string;
  owner:       string;
  deadline:    string;
}

function RcaViewModal({ incident, onClose }: { incident: IncidentWithRelations; onClose: () => void }) {
  const { data: rca, isLoading } = useQuery<RcaRow | null>({
    queryKey: ['rca', incident.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('quality_rca')
        .select('why_1, why_2, why_3, why_4, root_cause, action_plan, owner, deadline')
        .eq('incident_id', incident.id)
        .maybeSingle();
      return (data as RcaRow | null);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-black text-slate-800 dark:text-white">تحليل السبب الجذري — 5 لماذا</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : !rca ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-8 text-sm">لا يوجد تحليل جذري مسجل</p>
          ) : (
            <div className="space-y-4">
              {/* 5 Whys */}
              {[rca.why_1, rca.why_2, rca.why_3, rca.why_4].filter(Boolean).map((why, i) => (
                <div key={i} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-black flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-sm text-slate-700 dark:text-slate-300 pt-1">{why}</p>
                </div>
              ))}

              <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4">
                <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-1">السبب الجذري</p>
                <p className="text-sm text-slate-800 dark:text-white">{rca.root_cause}</p>
              </div>

              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-4">
                <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mb-1">خطة العمل التصحيحي</p>
                <p className="text-sm text-slate-800 dark:text-white whitespace-pre-wrap">{rca.action_plan}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">المسؤول</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300">{rca.owner}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">الموعد النهائي</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300">{rca.deadline}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   NotificationsModal
══════════════════════════════════════════════════════════════════ */

function NotificationsModal({
  notifications,
  onMarkRead,
  onClose,
}: {
  notifications: NotificationRow[];
  onMarkRead: (ids: string[]) => void;
  onClose:    () => void;
}) {
  const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-black text-slate-800 dark:text-white">الإشعارات</h2>
          <div className="flex items-center gap-2">
            {unreadIds.length > 0 && (
              <button onClick={() => onMarkRead(unreadIds)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                قراءة الكل
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {notifications.length === 0 ? (
            <p className="text-center text-slate-400 dark:text-slate-500 py-10 text-sm">لا توجد إشعارات</p>
          ) : notifications.map(n => (
            <div key={n.id} className={`p-4 ${!n.is_read ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}>
              <p className="text-sm font-bold text-slate-800 dark:text-white">{n.type}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{n.message}</p>
              <p className="text-[10px] text-slate-400 mt-1">{formatDate(n.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ProfileModal
══════════════════════════════════════════════════════════════════ */

function ProfileModal({ profile, onClose }: { profile: ProfileWithDept | null; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-black text-slate-800 dark:text-white">الملف الشخصي</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl font-black">
              {profile?.name ? profile.name.split(' ').map(w => w[0]).join('').slice(0,2) : '?'}
            </div>
            <div>
              <p className="font-black text-slate-800 dark:text-white text-lg">{profile?.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{profile?.role}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            {[
              { label: 'البريد الإلكتروني', value: (profile as any)?.email },
              { label: 'رقم الجوال',        value: profile?.phone },
              { label: 'القسم',              value: profile?.department?.name },
              { label: 'المسمى الوظيفي',     value: profile?.title },
            ].map(row => row.value ? (
              <div key={row.label} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-slate-400">{row.label}</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">{row.value}</span>
              </div>
            ) : null)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   LogoutModal
══════════════════════════════════════════════════════════════════ */

function LogoutModal({ onConfirm, onClose }: { onConfirm: () => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-lg font-black text-slate-800 dark:text-white text-center">تسجيل الخروج</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center">هل تريد تسجيل الخروج من النظام؟</p>
        <div className="flex gap-3">
          <button onClick={onClose}   className="btn-secondary flex-1">إلغاء</button>
          <button onClick={onConfirm} className="btn-danger    flex-1">تسجيل الخروج</button>
        </div>
      </div>
    </div>
  );
}
